import FrameEditPencil from "../../../../customize/components/FrameEditPencil";
import { useHeaderMediaEdit } from "./HeaderMediaEditContext";
import { HeaderMediaFrame } from "./getPrimaryHeaderImageUrl";
import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo, useEffect, useState } from "react";

const AUTO_ADVANCE_MS = 3000;
const SLIDE_TRANSITION_MS = 500;
/** Shared visual height with MarqueeMedia so multi-photo treatments align. */
const IMAGE_HEIGHT = styleUtils.rem(520);

const styles = {
  controls: css`
    bottom: ${theme.spacing.x4};
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.x2};
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    z-index: 1;
  `,
  // Height is locked so slides don't jump; cover crops to fill the frame.
  image: css`
    display: block;
    height: ${IMAGE_HEIGHT};
    object-fit: cover;
    width: 100%;
  `,
  indicator: (isActive: boolean) => css`
    background-color: ${isActive ? theme.colors.gray700 : theme.colors.gray300};
    border-style: none;
    cursor: pointer;
    height: ${styleUtils.rem(3)};
    padding-bottom: 0;
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
    width: ${styleUtils.rem(24)};
  `,
  // Legacy fancyclothes overlay hook; kept for parity with FullBleedMedia.
  shadow: css`
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  `,
  slide: css`
    flex: 0 0 100%;
    position: relative;
    width: 100%;
  `,
  track: (activeIndex: number, animate: boolean) => css`
    display: flex;
    flex-direction: row;
    transform: translateX(-${activeIndex * 100}%);
    transition: ${animate ? `transform ${SLIDE_TRANSITION_MS}ms ease` : "none"};
  `,
  viewport: css`
    margin-bottom: ${styleUtils.rem(30)};
    overflow: hidden;
    position: relative;
    width: 100%;
  `,
};

export type SlideshowMediaProps = {
  alt: string;
  frames: HeaderMediaFrame[];
};

/**
 * Slideshow media treatment — one full-width photo at a time with optional
 * auto-advance. In customize preview, each slide gets a pencil that travels
 * with the track and edits the matching headerImages slot.
 */
const SlideshowMedia = ({ alt, frames }: SlideshowMediaProps) => {
  const mediaEdit = useHeaderMediaEdit();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const slideCount = frames.length;
  // Derive when the gallery shrinks — avoid syncing setState in an effect.
  const safeActiveIndex =
    slideCount > 0 && activeIndex < slideCount ? activeIndex : 0;
  const showControls = slideCount > 1;
  const shouldAutoAdvance = showControls && !isHovered && !prefersReducedMotion;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    syncPreference();
    mediaQuery.addEventListener("change", syncPreference);

    return () => {
      mediaQuery.removeEventListener("change", syncPreference);
    };
  }, []);

  // safeActiveIndex in the dependency list resets the 3s timer after bar
  // clicks and after each auto-advance.
  useEffect(() => {
    if (!shouldAutoAdvance) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => {
        const normalized = currentIndex < slideCount ? currentIndex : 0;

        return (normalized + 1) % slideCount;
      });
    }, AUTO_ADVANCE_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [safeActiveIndex, shouldAutoAdvance, slideCount]);

  if (slideCount === 0) {
    return null;
  }

  return (
    <div
      css={styles.viewport}
      data-cy="slideshowMedia"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div css={styles.shadow} />
      <div
        css={styles.track(safeActiveIndex, !prefersReducedMotion)}
        data-cy="slideshowTrack"
      >
        {frames.map((frame, index) => (
          <div
            css={styles.slide}
            key={`${frame.url}-${frame.position}-${index}`}
          >
            <img
              alt={alt}
              css={styles.image}
              data-cy="slideshowImage"
              data-slot-index={frame.position}
              src={frame.url}
            />
            {mediaEdit ? (
              <FrameEditPencil
                onEdit={() => mediaEdit.onEditSlot(frame.position)}
              />
            ) : null}
          </div>
        ))}
      </div>
      {showControls && (
        <div
          aria-label="Slideshow controls"
          css={styles.controls}
          role="tablist"
        >
          {frames.map((frame, index) => {
            const isActive = index === safeActiveIndex;

            return (
              <button
                aria-label={`Show slide ${index + 1}`}
                aria-selected={isActive}
                css={styles.indicator(isActive)}
                data-cy="slideshowIndicator"
                key={`${frame.url}-indicator-${frame.position}`}
                onClick={() => setActiveIndex(index)}
                role="tab"
                type="button"
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const MemoizedSlideshowMedia = memo(SlideshowMedia);

export { MemoizedSlideshowMedia as SlideshowMedia };
